// Settings (settings prototype): every company rule the screens show, each said once (RULES); where
// Blueprint's Setup shows it, page by page and section by section (RULE_SECTIONS, from the setup audit of
// 24 Oct: every setting in one place, nothing lost and nothing twice); Riverstone's "Rules & defaults" as
// it has always been (RIVERSTONE_RULES); the roles matrix grouped for reading; and the small conversions
// the controls need.

import { financingWords, type FinancingSetting } from './financing-model.ts';

/** The parts of Settings, each its own page with its own link (settings/parts.ts names them). */
export type SettingsPage =
  | 'rules'
  | 'roles'
  | 'people'
  | 'time'
  | 'devices'
  | 'pricebook'
  | 'org'
  | 'tax'
  | 'templates'
  | 'forms'
  | 'calls'
  | 'campaigns'
  // Blueprint's own pages, made from Rules & defaults' rules (Riverstone keeps that one tab)
  | 'booking'
  | 'plans'
  | 'invoices'
  | 'costs'
  | 'payroll'
  | 'reviews'
  | 'agents'
  | 'connections';

export interface SettingRow {
  key: string;
  description: string;
  scopes: string[];
  value: unknown;
  source: string;
  schema: Record<string, unknown>;
  editPermission: string;
  overrides: { scopeType: string; scopeId: string; value: unknown }[];
}

export type Control =
  | { kind: 'bool' }
  | { kind: 'select'; options: readonly (readonly [string | number, string])[] }
  | { kind: 'number'; unit: string; min?: number; max?: number }
  /** `none`: it may be left empty, and these are its words then (the ad cap's "No cap"). */
  | { kind: 'money'; none?: string }
  | { kind: 'text'; type?: 'url' }
  | { kind: 'quietHours' }
  | { kind: 'workingHours' }
  | { kind: 'alertTo' }
  | { kind: 'methods' }
  | { kind: 'financing' }
  // the setup audit's new editors (patch 3): plain controls over settings that had none
  | { kind: 'paragraph'; keep?: readonly string[] }
  | { kind: 'words'; max: number; perLine?: true }
  | { kind: 'time' }
  | { kind: 'colour' }
  | { kind: 'overtime' }
  | { kind: 'twoFactor' }
  /** When the receptionist answers: outside the office's hours and days, or always. */
  | { kind: 'answerHours' };

export interface Rule {
  key: string;
  label: string;
  help?: string;
  control: Control;
  /** Q-number or audit finding the answer comes from. */
  q?: string;
  /** Only the owner changes it (its edit permission is owner-only, e.g. auth.manage). */
  ownerOnly?: true;
  /** It only matters while this rule (an on-or-off, in the same section) is on: Blueprint shows it
   *  under that rule, indented, and only then. */
  under?: string;
  /** A change with consequences: what it does, said before it applies (Blueprint saves each change
   *  as it's made, so these ask first). Null: this change needn't ask. */
  confirm?: (next: unknown, now: unknown) => string | null;
  /** A plain warning beside it for its saved value, never a refusal (a BC company's Monday week);
   *  `of` reads another rule's saved value. Null: nothing to say. */
  warn?: (value: unknown, of: (key: string) => unknown) => string | null;
}

/** A rule the product fixes: shown so people know, never a setting (F-01, Q8, Q44). */
export interface FixedRule {
  label: string;
  help?: string;
  q: string;
}

const WARN_OR_REFUSE = [
  ['warn', 'Warn, and send it anyway'],
  ['refuse', 'Refuse to send it'],
] as const;

// Each rule once, by its setting's key. A page shows it by naming the key in RULE_SECTIONS.
const DEFS = {
  'booking.defaultKind': {
    label: 'A newly booked job is',
    help: 'Unless the booking says otherwise.',
    control: {
      kind: 'select',
      options: [
        ['estimate_visit', 'An estimate visit'],
        ['service', 'Service work, already sold'],
      ],
    },
  },
  'booking.csrMayAssignTech': {
    label: 'The office may suggest a technician while booking',
    help: 'The dispatcher can move it.',
    control: { kind: 'bool' },
    q: 'Q15',
  },
  'appointments.tripChargeCents': {
    label: 'Trip charge for a no-show or a late cancel',
    help: 'Only when the tech is already on the way. The office can waive it, with a reason. 0 means none.',
    control: { kind: 'money' },
    q: 'Q43',
  },
  'dispatch.lateAfterMinutes': {
    label: 'Late or running long after',
    help: 'Dispatch marks a visit late when nobody is there this long after its arrival window closes, and running long when the work goes this far past its planned length.',
    control: { kind: 'number', unit: 'minutes', min: 0, max: 240 },
    q: 'BL-062',
  },
  'dispatch.idealBookingPercent': {
    label: 'An arrival window is full enough at',
    help: 'Book warns once a window’s booked time passes this share of its technicians’ time; it can still be booked.',
    control: { kind: 'number', unit: '%', min: 10, max: 100 },
    q: 'BL-057',
  },
  'dispatch.slotMinutes': {
    label: 'A dragged visit snaps to',
    help: 'Dragging a visit on the board moves it, and either end, by this much. So do the arrow keys.',
    control: {
      kind: 'select',
      options: [
        [5, '5 minutes'],
        [10, '10 minutes'],
        [15, '15 minutes'],
        [30, '30 minutes'],
      ],
    },
    q: 'BL-124',
  },
  'dispatch.licenceRule': {
    label: 'When nobody on a visit holds a licence the job needs',
    help: 'The job type says which licences (a gas fitter’s, a backflow tester’s); someone on the visit holds each, valid that day. Warn: it’s booked and sent, and shows what’s missing. Refuse: it can’t be assigned or sent until someone licensed is on it.',
    control: { kind: 'select', options: WARN_OR_REFUSE },
    q: 'BL-027',
  },
  'dispatch.hoursRule': {
    label: 'When a visit falls outside a technician’s hours, time off or a holiday',
    help: 'Warn: it’s booked and sent, and says who and why. Refuse: it can’t be assigned or sent. An after-hours call-out is outside hours by design, so only time off and holidays count for it.',
    control: { kind: 'select', options: WARN_OR_REFUSE },
    q: 'BL-014',
  },
  'invoices.waiveDiagnosticWhenSold': {
    label: 'Waive the diagnostic fee when work is sold the same day',
    help: 'Off keeps the fee on the invoice.',
    control: { kind: 'bool' },
    q: 'Q6',
  },
  'invoices.signatureRequired': {
    label: 'The customer signs to accept the finished work',
    help: 'Their signature on the invoice, under the acknowledgement paragraph. Where it’s missing, Review asks for it or for why; the technician is never stopped.',
    control: { kind: 'bool' },
  },
  'afterHours.windows': {
    label: 'Working hours',
    help: 'A visit that starts outside them is after hours.',
    control: { kind: 'workingHours' },
    q: 'F-12',
  },
  'field.lateMaterialDays': {
    label: 'A technician adds material to a job up to',
    help: 'Days after their last visit on it ended. Later, the office adds it.',
    control: { kind: 'number', unit: 'days after their visit', min: 1, max: 30 },
  },
  'geofence.enabled': {
    label: 'Arrive automatically when the iPad is at the address',
    control: { kind: 'bool' },
  },
  'geofence.arriveRadiusM': {
    under: 'geofence.enabled',
    label: 'Arrive within',
    control: { kind: 'number', unit: 'metres of the address' },
  },
  'geofence.arriveDwellSeconds': {
    under: 'geofence.enabled',
    label: 'After staying for',
    control: { kind: 'number', unit: 'seconds' },
  },
  'field.onTheWayTrigger': {
    label: 'A technician is on the way when',
    help: 'The drive time and the customer’s “on the way” text start then.',
    control: {
      kind: 'select',
      options: [
        ['office_dispatch', 'The office dispatches them'],
        ['tech_start', 'They tap Start driving on the iPad'],
      ],
    },
  },
  'field.undoArriveMinutes': {
    label: 'A technician can undo arriving for',
    help: 'For a wrong tap, or the iPad arriving on its own by mistake.',
    control: { kind: 'number', unit: 'minutes', min: 1, max: 60 },
    q: 'F-15',
  },
  'field.resumeSameDayOnly': {
    label: 'A paused visit is resumed the same day only',
    help: 'After that day, the office sends the technician out again.',
    control: { kind: 'bool' },
  },
  'field.lockAfterMinutes': {
    label: 'The iPad asks for the PIN again after',
    help: 'Minutes without a touch. It also locks once the app has been in the background for a minute.',
    control: { kind: 'number', unit: 'minutes', min: 1, max: 60 },
    q: 'QA-059',
  },
  'media.videoOnCellular': {
    label: 'Send videos over cellular data',
    help: 'Off: a video waits for Wi-Fi, unless the technician sends it now. Photos always go.',
    control: { kind: 'bool' },
  },
  'field.syncDaysBack': {
    label: 'Visits it keeps from the past',
    help: 'Paused and in-progress visits are always kept.',
    control: { kind: 'number', unit: 'days', min: 1, max: 30 },
  },
  'field.syncDaysAhead': {
    label: 'Scheduled visits it holds ahead',
    control: { kind: 'number', unit: 'days', min: 0, max: 14 },
  },
  'field.historyYears': {
    label: 'A customer’s past jobs, going back',
    help: 'A summary of each, without costs.',
    control: { kind: 'number', unit: 'years', min: 0, max: 10 },
  },
  'field.historyJobs': {
    label: 'At most, per customer',
    control: { kind: 'number', unit: 'past jobs', min: 0, max: 200 },
  },
  'messaging.onTheWay.enabled': {
    label: 'Send an “on the way” text when a visit is dispatched',
    control: { kind: 'bool' },
  },
  'payments.receipts.auto': {
    label: 'Send a receipt after each payment',
    help: 'By email if they agreed to email, else by text. Off: no receipt goes on its own.',
    control: { kind: 'bool' },
  },
  'calls.textBack.enabled': {
    label: 'Text a missed caller back',
    help: 'Once a day per number, only to a mobile that agreed to texts; never in quiet hours, to do-not-contact, or to a number marked spam or wrong number; not once they’ve been answered or called back.',
    control: { kind: 'bool' },
  },
  'calls.textBack.delayMinutes': {
    under: 'calls.textBack.enabled',
    label: 'After',
    help: 'So a quick call back from the office comes first.',
    control: { kind: 'number', unit: 'minutes', min: 1, max: 30 },
  },
  'calls.textBack.words': {
    under: 'calls.textBack.enabled',
    label: 'Its words',
    help: '{companyName} is the company’s name.',
    control: { kind: 'text' },
  },
  'messaging.quietHours': {
    label: 'Quiet hours for automatic texts',
    help: 'Agents’ texts wait until they end. People in the office are not held to them.',
    control: { kind: 'quietHours' },
  },
  'reviews.linkPolicy': {
    label: 'Who is offered the Google review link',
    help: 'Everyone who rates a job sees the link, without being pushed to use it.',
    control: {
      kind: 'select',
      options: [
        ['all_raters', 'Every rater'],
        ['five_star_only', '5 stars only (review gating)'],
      ],
    },
    q: 'Q1',
  },
  'reviews.link': { label: 'Review page', control: { kind: 'text', type: 'url' } },
  'reviews.ratingDelayMinutes': {
    label: 'Send the rating text',
    control: { kind: 'number', unit: 'minutes after the job is completed', min: 0, max: 10080 },
  },
  'reviews.lowRatingThreshold': {
    label: 'A low rating is',
    control: {
      kind: 'select',
      options: [
        [1, '1 star'],
        [2, '1 or 2 stars'],
        [3, '1 to 3 stars'],
        [4, '1 to 4 stars'],
      ],
    },
  },
  'reviews.lowRatingAlertTo': { label: 'A low rating alerts', control: { kind: 'alertTo' } },
  'payments.financing': {
    label: 'Consumer financing',
    help: 'The lender and the plans you offer. Options show “as low as $X a month” from them, with the link a customer applies on. With no plans, no monthly payment is shown.',
    control: { kind: 'financing' },
    q: 'BL-005',
  },
  'estimates.techMaxDiscountPct': {
    label: 'A tech may discount on their own by up to',
    help: 'Deeper discounts, or anything below our cost, ask the office first.',
    control: { kind: 'number', unit: '% below book price', min: 0, max: 100 },
    q: 'Q44',
  },
  'estimates.freeFormFloorCents': {
    label: 'A line not from the pricebook needs the office’s OK below',
    control: { kind: 'money' },
  },
  'estimates.minMarginPct': {
    label: 'An option under this margin needs the office’s OK',
    help: 'Its price less our cost, over its price, on the lines that have a cost. 0 means off. An option with no costed line isn’t checked.',
    control: { kind: 'number', unit: '% margin', min: 0, max: 95 },
    q: 'BL-059',
  },
  'estimates.warnMarginPct': {
    label: 'The margin we aim for',
    help: 'Under it, those who see cost are told on an option, which sells as usual, and Profit marks the jobs and technicians under it. 0 means off.',
    control: { kind: 'number', unit: '% margin', min: 0, max: 95 },
    q: 'BL-059',
  },
  'costing.labourBurden': {
    label: 'Paid labour costs on top of the pay',
    help: 'The employer’s CPP and EI, WorkSafeBC and vacation pay, as a share of the pay. Every job’s labour carries it, so its gross profit is what the job really made.',
    control: { kind: 'number', unit: '% of the pay', min: 0, max: 100 },
  },
  'estimates.depositPercent': {
    label: 'When an estimate is signed for later, suggest a deposit of',
    help: 'Of the signed total. 0 suggests none; a deposit can still be taken.',
    control: { kind: 'number', unit: '% of the signed total', min: 0, max: 100 },
    q: 'BL-006',
  },
  'portal.estimateLinkDays': {
    label: 'An estimate’s link works for',
    help: 'The page a customer opens to read, sign and pay a deposit.',
    control: { kind: 'number', unit: 'days', min: 1, max: 365 },
  },
  'portal.invoiceLinkDaysAfterPaid': {
    label: 'An invoice’s link works once it’s paid for',
    control: { kind: 'number', unit: 'days', min: 1, max: 365 },
  },
  'purchasing.approvalLimitCents': {
    label: 'The warehouse sends purchase orders on its own up to',
    help: 'Above this, the owner approves.',
    control: { kind: 'money' },
    q: 'Q32',
  },
  'huddle.revenueBasis': {
    label: 'The huddle counts revenue',
    help: 'Match how the month’s goals were set.',
    control: {
      kind: 'select',
      options: [
        ['pre_tax', 'Before sales tax'],
        ['with_tax', 'With sales tax'],
      ],
    },
    ownerOnly: true,
  },
  'memberships.renewalReminderDays': {
    label: 'Remind the customer before a renewal',
    help: 'With the amount, and the card it goes on.',
    control: { kind: 'number', unit: 'days before', min: 1, max: 60 },
  },
  'memberships.renewalTries': {
    label: 'Try a declined renewal',
    help: 'Then it stops, and the membership shows as at risk for the office.',
    control: { kind: 'number', unit: 'times', min: 1, max: 10 },
  },
  'memberships.renewalRetryDays': {
    label: 'Between tries, wait',
    control: { kind: 'number', unit: 'days', min: 1, max: 14 },
  },
  'payroll.sickPaidDays': {
    label: 'Paid sick days a year',
    help: 'At the average day’s pay, after 90 days of work. BC’s is 5; more is your own policy.',
    control: { kind: 'number', unit: 'days', min: 0, max: 60 },
    ownerOnly: true,
  },
  'payroll.statEligibilityDays': {
    label: 'A statutory holiday is paid to who worked',
    help: 'Or earned wages, on this many of the 30 days before it. BC’s is 15.',
    control: { kind: 'number', unit: 'of the 30 days before', min: 0, max: 30 },
    ownerOnly: true,
  },
  'payroll.statHoursInWeek': {
    label: 'A worked statutory holiday counts toward the week’s overtime',
    help: 'Its first 8 hours, as any day’s. Never two premiums either way.',
    control: { kind: 'bool' },
    ownerOnly: true,
  },
  'time.staleFactHours': {
    label: 'A time typed in late is flagged after',
    help: 'Typed this long after it happened, it’s kept, and marked for the payroll review.',
    control: { kind: 'number', unit: 'hours', min: 1, max: 720 },
    q: 'QA-017',
  },
  'agents.receptionist.hours': {
    label: 'The receptionist answers',
    help: 'In office hours, calls ring the office first.',
    control: { kind: 'answerHours' },
    ownerOnly: true,
    confirm: (next, now) =>
      (next as AnswerHours).mode === 'always' && (now as AnswerHours).mode !== 'always'
        ? 'The receptionist answers every call, day and night: none ring the office first.'
        : null,
  },
  'calls.recordingRetentionMonths': {
    label: 'Keep call audio and full transcripts for',
    help: 'Then both are deleted; the AI summary stays.',
    confirm: (next, now) =>
      Number(next) < Number(now)
        ? `Call audio and full transcripts older than ${Number(next) / 12 === 1 ? '1 year' : `${Number(next) / 12} years`} are deleted for good. The AI summaries stay.`
        : null,
    control: {
      kind: 'select',
      options: [
        [12, '1 year'],
        [24, '2 years'],
        [36, '3 years'],
      ],
    },
    q: 'Q18',
  },
  'auth.methods': {
    label: 'People may sign in with',
    help: 'The owner can always sign in with an email code, whatever is chosen here.',
    control: { kind: 'methods' },
    confirm: (next, now) => {
      const gone = SIGN_IN_METHODS.filter(
        ([m]) => (now as string[]).includes(m) && !(next as string[]).includes(m),
      ).map(([, l]) => l.replace(/^A /, 'a '));
      return gone.length
        ? `Nobody can sign in with ${gone.join(' or ')} any more. The owner can still use an email code.`
        : null;
    },
    ownerOnly: true,
  },
  // the setup audit's new editors: settings the server has had with no control on any screen
  'auth.twoFactor': {
    label: 'Two-factor sign-in',
    help: 'The owner and anyone who handles money, roles or the data export always turn it on within 7 days.',
    control: { kind: 'twoFactor' },
    ownerOnly: true,
    // asks only when it covers more people, or allows fewer ways
    confirm: (next, now) => {
      const n = next as TwoFactor;
      const o = now as TwoFactor;
      const more =
        (n.mode === 'everyone' && o.mode !== 'everyone') ||
        (n.mode === 'roles' &&
          (o.mode === 'off' || o.mode === 'optional' || n.roles.some((r) => !o.roles.includes(r)))) ||
        (n.appOnly && !o.appOnly);
      return more ? 'The people it covers need a second step every time they sign in.' : null;
    },
  },
  'memberships.autoRenewTerms': {
    label: 'The auto-renew terms a customer agrees to',
    help: 'Keep {amount} and {period}: the plan fills them in. A consent keeps the exact words shown.',
    control: { kind: 'paragraph', keep: ['{amount}', '{period}'] },
  },
  'payroll.overtime': {
    label: 'Overtime starts after',
    help: 'Time and a half past the day’s hours, double time past the longer day, time and a half past the week’s straight hours (BC: 8, 12 and 40).',
    control: { kind: 'overtime' },
    ownerOnly: true,
  },
  'payroll.weekStartsOn': {
    label: 'The payroll week starts on',
    help: 'For the week’s overtime.',
    warn: (v, of) => weekWarning(Number(v), isBcRules(of('payroll.overtime') as Overtime | undefined)),
    control: {
      kind: 'select',
      options: [
        [0, 'Sunday'],
        [1, 'Monday'],
        [2, 'Tuesday'],
        [3, 'Wednesday'],
        [4, 'Thursday'],
        [5, 'Friday'],
        [6, 'Saturday'],
      ],
    },
    ownerOnly: true,
  },
  'payroll.provider': {
    label: 'Who runs your payroll',
    help: 'The provider’s name. Empty: none yet. Getting started asks it with the pay schedule.',
    control: { kind: 'text' },
    ownerOnly: true,
  },
  'messaging.onTheWay.template': {
    under: 'messaging.onTheWay.enabled',
    label: 'The on-the-way text',
    help: 'Fills in {customerName}, {techFirstName}, {companyName}, {window} and {techBioLink}.',
    control: { kind: 'paragraph' },
  },
  'appointments.reminders.enabled': {
    label: 'Remind each customer the day before their visit',
    help: 'With the arrival window; they reply to confirm. Only to a phone or email that agreed.',
    control: { kind: 'bool' },
  },
  'appointments.reminders.hour': {
    under: 'appointments.reminders.enabled',
    label: 'Day-before reminders go at',
    help: 'Between 8 a.m. and 8 p.m., the company’s time.',
    control: { kind: 'time' },
  },
  'appointments.reminders.confirmWords': {
    under: 'appointments.reminders.enabled',
    label: 'A reply that confirms the visit',
    help: 'Any of these, as the whole reply, in any case. Never STOP or START: they change consent.',
    control: { kind: 'words', max: 5 },
  },
  'estimates.termsVersion': {
    label: 'The terms version signatures record',
    help: 'Change it when the terms change: each signature keeps the version it was signed under.',
    control: { kind: 'text' },
  },
  'company.taxNumbers': {
    label: 'Tax numbers on invoices',
    help: 'Up to four, one a line, as they print: “GST 123456789 RT0001”.',
    control: { kind: 'words', max: 4, perLine: true },
  },
  'ads.monthlyCap': {
    label: 'Spend at most this a month on ads',
    help: 'Google Ads, Microsoft Ads and Local Services together. The weekly ads review suggests lowering a budget when the month heads over it. Empty: no cap.',
    control: { kind: 'money', none: 'No cap' },
  },
  'migration.mirrorEveryMinutes': {
    label: 'Copy from ServiceTitan every',
    help: 'ServiceTitan’s rate limits can stretch it for a while, up to an hour.',
    control: { kind: 'number', unit: 'minutes', min: 1, max: 60 },
  },
  'migration.mirrorLagAlertMinutes': {
    label: 'Tell the office when a copy is overdue by',
    help: 'Once a pass is this many minutes late: a list Flowology copies from ServiceTitan not read whole for the minutes between passes and this many more, or unfinished that long. It clears itself once the copy catches up.',
    control: { kind: 'number', unit: 'minutes', min: 10, max: 1440 },
  },
  // Setup › Price book › Pictures (BL-141): the switches for price book pictures (BL-109); the models,
  // quality and where a picture passes its check stay Flowology's (EDITED_ELSEWHERE)
  'pricebook.art.enabled': {
    label: 'Make a picture for each price book item',
    help: 'Drawn in Flowology’s house style; each costs a few cents to make. Off: nothing is made or spent.',
    control: { kind: 'bool' },
    q: 'BL-141',
    confirm: (next, now) =>
      next === true && now !== true
        ? 'Flowology starts making pictures for your price book items, a few cents each, up to the month’s cap.'
        : null,
  },
  'pricebook.art.autoForNewItems': {
    under: 'pricebook.art.enabled',
    label: 'Make one for each new item on its own',
    help: 'Off: a new item waits until someone makes the missing ones.',
    control: { kind: 'bool' },
    q: 'BL-141',
  },
  // BL-122: shown whether pictures are on or off, so it's set before the first picture is made
  'pricebook.art.requireApproval': {
    label: 'New pictures wait for approval',
    help: 'The owner or a manager approves each new picture under To approve before customers and technicians see it. Off: a picture that passes its check shows straight away.',
    control: { kind: 'bool' },
    q: 'BL-122',
  },
  'pricebook.art.monthlyCapCents': {
    label: 'Spend at most this a month on pictures',
    help: 'Items that look alike share one picture. Past it, making waits for the next month. 0 makes none.',
    control: { kind: 'money' },
    q: 'BL-141',
  },
  'pricebook.art.library.contribute': {
    label: 'Share our pictures with Flowology’s picture library',
    help: 'So other companies use them rather than make their own. Only the picture goes: never an item, a name or anyone’s request.',
    control: { kind: 'bool' },
    q: 'BL-141',
  },
  // Amir 25 Oct: which an item shows first when it has both; shown whether pictures are on or off, as
  // the drawings already made still show
  'pricebook.art.showFirst': {
    label: 'Picture shown first',
    help: 'When an item has both a drawing and an imported photo. The other one stays on the item.',
    control: {
      kind: 'select',
      options: [
        ['drawing', 'Flowology drawing'],
        ['imported', 'Imported photo (from your previous system)'],
      ],
    },
    q: 'BL-109',
  },
  // Home › Performance's targets (BL-123), marked on each scorecard
  'reports.conversionTarget': {
    label: 'A technician’s conversion target',
    help: 'Of their opportunities, how many sold.',
    control: { kind: 'number', unit: '%', min: 0, max: 100 },
    q: 'BL-123',
  },
  'reports.membershipTarget': {
    label: 'A technician’s membership target',
    help: 'Of the customers they served without a membership, how many joined.',
    control: { kind: 'number', unit: '%', min: 0, max: 100 },
    q: 'BL-123',
  },
  'reports.bookingTarget': {
    label: 'The phones’ booking target',
    help: 'Of the calls that could be booked, how many were.',
    control: { kind: 'number', unit: '%', min: 0, max: 100 },
    q: 'BL-123',
  },
  // the maps' switch (BL-018): an office map with it off says so and links here (QA-756)
  'maps.google': {
    label: 'Use Google’s map and checked addresses',
    help: 'Google’s map on the Schedule, the job page and each address, with address suggestions and drive times. Off: the pins are on a plain grid and addresses are plain fields.',
    control: { kind: 'bool' },
    q: 'BL-018',
  },
} satisfies Record<string, Omit<Rule, 'key'>>;

type RuleKey = keyof typeof DEFS;

/** Every rule a page shows, by its setting's key. */
export const RULES: Readonly<Record<RuleKey, Rule>> = Object.fromEntries(
  Object.entries(DEFS).map(([key, d]) => [key, { key, ...d } as Rule]),
) as Record<RuleKey, Rule>;

const rules = (...keys: RuleKey[]): Rule[] => keys.map((k) => RULES[k]);

const CONSENT: FixedRule = {
  label: 'Only text or email people who agreed to it',
  help: 'No text consent: no texts. Do-not-contact: nothing automatic.',
  q: 'F-01',
};
const OVERRIDE_REASON: FixedRule = { label: 'A price override always needs a reason', q: 'Q8' };
const NEVER_NEGATIVE: FixedRule = {
  label: 'Never below $0',
  help: 'A price can be discounted, never made negative.',
  q: 'Q44',
};

export interface RuleGroup {
  title: string;
  rules: readonly Rule[];
  fixed?: readonly FixedRule[];
}

/** Riverstone's Rules & defaults, as it has always been: its groups, in their order. */
export const RIVERSTONE_RULES: readonly RuleGroup[] = [
  {
    title: 'Booking and visits',
    rules: rules(
      'booking.defaultKind',
      'booking.csrMayAssignTech',
      'appointments.tripChargeCents',
      'dispatch.lateAfterMinutes',
      'dispatch.idealBookingPercent',
      'dispatch.licenceRule',
      'invoices.waiveDiagnosticWhenSold',
      'invoices.signatureRequired',
      'afterHours.windows',
      'field.lateMaterialDays',
      'geofence.enabled',
      'geofence.arriveRadiusM',
      'geofence.arriveDwellSeconds',
    ),
  },
  {
    title: 'Messages and consent',
    rules: rules(
      'messaging.onTheWay.enabled',
      'calls.textBack.enabled',
      'calls.textBack.delayMinutes',
      'calls.textBack.words',
      'messaging.quietHours',
    ),
    fixed: [CONSENT],
  },
  {
    title: 'Reviews and ratings',
    rules: rules(
      'reviews.linkPolicy',
      'reviews.link',
      'reviews.ratingDelayMinutes',
      'reviews.lowRatingThreshold',
      'reviews.lowRatingAlertTo',
    ),
  },
  {
    title: 'Prices and purchasing',
    rules: rules(
      'payments.financing',
      'estimates.techMaxDiscountPct',
      'estimates.freeFormFloorCents',
      'estimates.minMarginPct',
      'estimates.warnMarginPct',
      'estimates.depositPercent',
      'purchasing.approvalLimitCents',
    ),
    fixed: [OVERRIDE_REASON, NEVER_NEGATIVE],
  },
  { title: 'Job costing', rules: rules('costing.labourBurden') },
  { title: 'Calls and recordings', rules: rules('calls.recordingRetentionMonths') },
  { title: 'Signing in', rules: rules('auth.methods') },
];

/** The keys Riverstone's Rules & defaults shows as rules; the rest go in its "Other settings". */
export const RIVERSTONE_RULE_KEYS = new Set(RIVERSTONE_RULES.flatMap((g) => g.rules.map((r) => r.key)));

/** The pages a rule section sits on: Settings' parts, and Company details. */
export type RulePage = SettingsPage | 'company' | 'migration';

export interface RuleSection extends RuleGroup {
  id: string;
  page: RulePage;
  /** Drawn before the page's own lists (Hours and holidays opens on the working hours). */
  top?: true;
  /** Its rows go inside the page's own list of the same name (Texts and email's words), not a section. */
  inPart?: true;
  /** Blueprint: one line under the heading saying what the section changes. */
  line?: string;
}

/**
 * Blueprint's Setup (the setup audit, 24 Oct): each rule on the page and in the section where its
 * subject is, so a page shows the rules beside the lists they govern. A rule moves with one line. A
 * section with no rules yet holds a place for those being added (labour burden, the ad cap) and draws
 * nothing until it has one. Customer pages' design has no section: they are Blueprint (Amir, 25 Oct).
 */
export const RULE_SECTIONS: readonly RuleSection[] = [
  // Your company
  // the terms version, inside On your paperwork beside the signed words
  {
    id: 'company.paper',
    page: 'company',
    title: 'On your paperwork',
    inPart: true,
    rules: rules('estimates.termsVersion'),
  },
  {
    id: 'roles.signin',
    page: 'roles',
    title: 'How people sign in',
    line: 'The ways people may sign in, and when they need a second step.',
    rules: rules('auth.methods', 'auth.twoFactor'),
  },
  {
    id: 'time.hours',
    page: 'time',
    // headed by what it decides, so the heading doesn't repeat its one row's label
    title: 'After hours',
    line: 'A call or a visit outside your working hours is after hours.',
    top: true,
    rules: rules('afterHours.windows'),
  },
  // Booking and jobs
  {
    id: 'booking.booking',
    page: 'booking',
    title: 'Booking',
    line: 'What a new booking starts as, and who suggests its technician.',
    rules: rules('booking.defaultKind', 'booking.csrMayAssignTech'),
  },
  {
    id: 'booking.dispatch',
    page: 'booking',
    title: 'Dispatch',
    line: 'When a visit counts as late or full, what dispatch lets through, and the board’s slot.',
    rules: rules(
      'dispatch.lateAfterMinutes',
      'dispatch.idealBookingPercent',
      'dispatch.slotMinutes',
      'dispatch.licenceRule',
      'dispatch.hoursRule',
    ),
  },
  {
    id: 'booking.arriving',
    page: 'booking',
    title: 'On the way and arriving',
    line: 'When a technician is on the way, and when they have arrived.',
    rules: rules(
      'field.onTheWayTrigger',
      'geofence.enabled',
      'geofence.arriveRadiusM',
      'geofence.arriveDwellSeconds',
      'field.undoArriveMinutes',
    ),
  },
  // the maps' switch (QA-756): a map with maps off links here; the drive check's rules join it with
  // Setup › Maps and addresses (the maps design's package 10)
  {
    id: 'booking.maps',
    page: 'booking',
    title: 'Maps',
    line: 'Whether the office uses Google’s map and checked addresses.',
    rules: rules('maps.google'),
  },
  // the targets Home › Performance marks (BL-123): the technicians' and the phones'
  {
    id: 'booking.targets',
    page: 'booking',
    title: 'Targets',
    line: 'The targets Home › Performance marks on each technician’s scorecard and on the phones.',
    rules: rules('reports.conversionTarget', 'reports.membershipTarget', 'reports.bookingTarget'),
  },
  {
    id: 'devices.job',
    page: 'devices',
    title: 'On the job',
    line: 'What the iPad allows on the job, and when it locks.',
    rules: rules(
      'field.lateMaterialDays',
      'field.resumeSameDayOnly',
      'field.lockAfterMinutes',
      'media.videoOnCellular',
    ),
  },
  {
    id: 'devices.keeps',
    page: 'devices',
    title: 'What the iPad keeps',
    line: 'How much the iPad holds, so it works without signal.',
    rules: rules('field.syncDaysBack', 'field.syncDaysAhead', 'field.historyYears', 'field.historyJobs'),
  },
  // Prices and money
  {
    id: 'pricebook.limits',
    page: 'pricebook',
    title: 'A technician’s price limits',
    line: 'How far a technician may go on price before the office says yes.',
    rules: rules('estimates.techMaxDiscountPct', 'estimates.freeFormFloorCents', 'estimates.minMarginPct'),
    fixed: [OVERRIDE_REASON, NEVER_NEGATIVE],
  },
  // the switches over price book pictures (BL-141), with the book's picture status under them
  // (Settings.tsx: PricebookArtLine inside); regenerate's refusal sends people here
  {
    id: 'pricebook.pictures',
    page: 'pricebook',
    title: 'Pictures',
    line: 'Whether Flowology draws a picture for each item, who approves each new one, the most it may spend a month, and which picture an item shows first.',
    rules: rules(
      'pricebook.art.enabled',
      'pricebook.art.autoForNewItems',
      'pricebook.art.requireApproval',
      'pricebook.art.monthlyCapCents',
      'pricebook.art.library.contribute',
      'pricebook.art.showFirst',
    ),
  },
  {
    id: 'plans.renewals',
    page: 'plans',
    title: 'Renewals',
    line: 'How memberships renew, and the terms a customer agrees to.',
    rules: rules(
      'memberships.renewalReminderDays',
      'memberships.renewalTries',
      'memberships.renewalRetryDays',
      'memberships.autoRenewTerms',
    ),
  },
  {
    id: 'invoices.charges',
    page: 'invoices',
    title: 'Charges',
    line: 'Fees an invoice adds, and when they are waived.',
    rules: rules('appointments.tripChargeCents', 'invoices.waiveDiagnosticWhenSold'),
  },
  {
    id: 'invoices.paid',
    page: 'invoices',
    title: 'Getting paid',
    line: 'Deposits, financing, and how long payment links work.',
    rules: rules(
      'estimates.depositPercent',
      'payments.financing',
      'portal.estimateLinkDays',
      'portal.invoiceLinkDaysAfterPaid',
    ),
  },
  {
    id: 'invoices.signed',
    page: 'invoices',
    title: 'Signatures and numbers',
    line: 'What the customer signs, and how documents are numbered.',
    rules: rules('invoices.signatureRequired'),
  },
  {
    id: 'costs.costing',
    page: 'costs',
    title: 'Job costing',
    line: 'The margin jobs are measured against, and what labour really costs.',
    rules: rules('estimates.warnMarginPct', 'costing.labourBurden', 'huddle.revenueBasis'),
  },
  {
    id: 'costs.po',
    page: 'costs',
    title: 'Purchase orders',
    line: 'How large a purchase order the warehouse sends on its own.',
    rules: rules('purchasing.approvalLimitCents'),
  },
  // what most open Payroll rules for, first (the prototype's order)
  {
    id: 'payroll.overtime',
    page: 'payroll',
    title: 'Overtime',
    line: 'When overtime starts, and the week it is counted in.',
    rules: rules('payroll.overtime', 'payroll.weekStartsOn'),
  },
  {
    id: 'payroll.sick',
    page: 'payroll',
    title: 'Sick days and stat holidays',
    line: 'Paid sick days, and who is paid for a statutory holiday.',
    rules: rules('payroll.sickPaidDays', 'payroll.statEligibilityDays', 'payroll.statHoursInWeek'),
  },
  {
    id: 'payroll.provider',
    page: 'payroll',
    title: 'Payroll provider',
    line: 'Who runs your payroll, if anyone yet.',
    rules: rules('payroll.provider'),
  },
  {
    id: 'payroll.time',
    page: 'payroll',
    title: 'Time entries',
    line: 'When a time typed in late is flagged for the payroll review.',
    rules: rules('time.staleFactHours'),
  },
  // Customers and calls
  {
    id: 'templates.words',
    page: 'templates',
    title: 'The words',
    inPart: true,
    rules: [],
  },
  {
    id: 'templates.auto',
    page: 'templates',
    title: 'Sent on their own',
    line: 'The texts and emails that go without anyone pressing send.',
    rules: rules(
      'messaging.onTheWay.enabled',
      'messaging.onTheWay.template',
      'payments.receipts.auto',
      'appointments.reminders.enabled',
      'appointments.reminders.hour',
      'appointments.reminders.confirmWords',
    ),
  },
  {
    id: 'templates.quiet',
    page: 'templates',
    title: 'Quiet hours and consent',
    line: 'When automatic texts wait, and who they never go to.',
    rules: rules('messaging.quietHours'),
    fixed: [CONSENT],
  },
  {
    id: 'reviews.asking',
    page: 'reviews',
    title: 'Asking for a review',
    line: 'Who is asked for a review, where it goes, and when.',
    rules: rules('reviews.linkPolicy', 'reviews.link', 'reviews.ratingDelayMinutes'),
  },
  {
    id: 'reviews.low',
    page: 'reviews',
    title: 'Low ratings',
    line: 'What counts as a low rating, and who hears about it.',
    rules: rules('reviews.lowRatingThreshold', 'reviews.lowRatingAlertTo'),
  },
  // Calls opens with what a missed caller gets and who answers, then the reasons list (the prototype)
  {
    id: 'calls.missed',
    page: 'calls',
    top: true,
    title: 'Missed calls',
    line: 'Whether a missed caller gets a text back, when, and what it says.',
    rules: rules('calls.textBack.enabled', 'calls.textBack.delayMinutes', 'calls.textBack.words'),
  },
  {
    id: 'calls.recordings',
    page: 'calls',
    top: true,
    title: 'The receptionist and recordings',
    line: 'When the receptionist answers, and how long recordings are kept.',
    rules: rules('agents.receptionist.hours', 'calls.recordingRetentionMonths'),
  },
  // the tax numbers print on every invoice, under the zones' rates
  {
    id: 'tax.numbers',
    page: 'tax',
    title: 'On every invoice',
    line: 'The tax numbers printed on every invoice.',
    rules: rules('company.taxNumbers'),
  },
  // Connections
  {
    id: 'migration.copy',
    page: 'migration',
    title: 'How often it copies',
    line: 'How often Flowology copies from ServiceTitan, and when the office hears it’s behind.',
    rules: rules('migration.mirrorEveryMinutes', 'migration.mirrorLagAlertMinutes'),
  },
  {
    // a part of Ad spend, under its entries and links (Settings.tsx): the page keeps to 3 zones
    id: 'campaigns.spend',
    page: 'campaigns',
    title: 'Monthly cap',
    inPart: true,
    line: 'The most the company means to spend on ads in a month.',
    rules: rules('ads.monthlyCap'),
  },
];

/** A setting Find a setting turns up: its row's label, and where it is (its page and section). */
export interface FoundSetting {
  key: string;
  label: string;
  section: string;
  page: RulePage;
}

/**
 * Find a setting (Company details): the rules with every word typed somewhere in their label, their
 * help or their section's heading, on the pages this person opens (`opens`), at most `max` of them.
 */
export function findSettings(query: string, opens: (page: RulePage) => boolean, max = 8): FoundSetting[] {
  // a word's stem too, so "reminders" finds "Remind each customer" and "texts" finds "text"
  const stem = (w: string) => {
    const t = w.replace(/(ers|er|ing|ed|s)$/, '');
    return t.length >= 4 ? t : w;
  };
  const words = query
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length > 1)
    .map(stem);
  if (!words.length) return [];
  const found: FoundSetting[] = [];
  for (const s of RULE_SECTIONS) {
    if (!opens(s.page)) continue;
    for (const r of s.rules) {
      // a dependent ("After") is known by the rule it depends on too
      const parent = r.under ? s.rules.find((x) => x.key === r.under) : undefined;
      const label = parent ? `${parent.label} › ${r.label}` : r.label;
      const hay = `${label} ${r.help ?? ''} ${s.title}`.toLowerCase();
      if (words.every((w) => hay.includes(w)))
        found.push({ key: r.key, label, section: s.title, page: s.page });
    }
  }
  return found.slice(0, max);
}

/** A page's rule sections, in order: those before its own lists, or those after. */
export const sectionsOf = (page: RulePage, top = false): RuleSection[] =>
  RULE_SECTIONS.filter((s) => s.page === page && !!s.top === top && !s.inPart);

/** A rule section by its id. */
export const ruleSection = (id: string): RuleSection | undefined => RULE_SECTIONS.find((s) => s.id === id);

/**
 * The settings no rule section shows, each with where it is changed instead, or why nobody changes it
 * here. With RULE_SECTIONS, every setting the server has is in exactly one of the two (the setup audit's
 * "nothing lost, nothing twice", test/setup-homes.test.ts).
 */
export const EDITED_ELSEWHERE: Readonly<Record<string, string>> = {
  // the customer text, the company's and a department's own (BL-052): Company details › On your paperwork
  'company.officialName': 'the customer text',
  'company.phone': 'the customer text (and Getting started’s first step)',
  'company.email': 'the customer text',
  'company.addressLines': 'the customer text',
  'company.licenceNumber': 'the customer text',
  'documents.invoiceHeader': 'the customer text',
  'documents.invoiceFooter': 'the customer text',
  'estimates.termsText': 'the customer text',
  'invoices.acknowledgementText': 'the customer text',
  // their own editors
  'agents.policy.*': 'AI agents › What each agent may do',
  'agents.limit.*': 'AI agents › What each agent may do',
  'agents.budget.warnPercent': 'AI agents › Budget',
  'huddle.targets': 'Home › The month so far',
  'accounting.fiscalYearStartMonth': 'Books setup',
  'payments.etransferBankAccountId': 'Books setup',
  'ads.conversions': 'its own action (marketing.setConversions), which takes the first day',
  'ads.conversionActions': 'Campaigns › Spend and conversions › Conversions to Google Ads (BL-091)',
  'ads.site': 'Campaigns › Your website (its own action, adSessions.setSite; BL-092)',
  // Campaigns: what rings when a tracking number goes unanswered (BL-001), its own part
  'calls.mainLine': 'Campaigns › When nobody answers',
  'calls.ringSeconds': 'Campaigns › When nobody answers',
  'calls.recordForwarded': 'Campaigns › When nobody answers',
  // the calls Flowology answers (docs/specs/live-transcripts.md): set by Flowology until their editor
  'calls.greeting':
    'no editor yet: Plays when we answer, before anyone speaks. Privacy guidance in Canada expects callers to be told when calls are recorded or transcribed.',
  'calls.liveTranscripts': 'no editor yet: Flowology turns it on for a company whose calls ring through it',
  // set for the company, never by it
  'company.trade': 'chosen at sign-up; only the starter price book reads it',
  'payments.terminalDeviceCode': 'Flowology connects the card terminal',
  // the board's Group menu sets it (Make this the company’s way), beside the grouping it describes
  'dispatch.openGrouped': 'Schedule › the board’s Group menu (Make this the company’s way)',
  'design.language': 'Blueprint for every company (DEC-008); there is no switch',
  'design.portal': 'customer pages are Blueprint for every company (Amir, 25 Oct); there is no switch',
  'branding.primaryColor': 'no editor yet (Company details › How you look)',
  // Sales tax › Tax zones: "Invoices use this" is chosen on the zones' list
  'tax.defaultZoneId': 'Sales tax › Tax zones',
  // no editor yet, each more than a plain control (patch 3's follow-ups)
  'booking.draftInvoiceLines': 'no editor yet: a list of price book items (Invoices and payments › Charges)',
  'receivables.reminderSteps': 'no editor yet: a list of steps (Invoices and payments › Getting paid)',
  'ads.conversionValue': 'no editor yet: with Google conversions (Ads and tracking numbers)',
  // the audit merges these into the company's own holidays and working hours; until the huddle reads
  // those instead (server work), they have no editor
  'huddle.closedDays': 'no editor yet (to be read from Holidays)',
  'huddle.workingWeekdays': 'no editor yet (to be read from Working hours)',
  // which job types are opportunities (BL-123): set on each job type, not for the company
  'reports.countsAsOpportunity': 'no editor yet: set on each job type (Job types)',
  // price book pictures (BL-109): the switches are Setup › Price book › Pictures (BL-141); these have no
  // editor yet, so each is the server's default until settings.set changes it
  'pricebook.art.quality': 'no editor yet: how finely each price book picture is made',
  'pricebook.art.styleReferences': 'no editor yet: the approved pictures the house style matches',
  'pricebook.art.model': 'no editor yet: the image model, Flowology’s default',
  'pricebook.art.checkModel': 'no editor yet: the model that checks each picture, Flowology’s default',
  'pricebook.art.jev.model':
    'no editor yet: the model that judges the price book’s words, Flowology’s default',
  'pricebook.art.jev.samePicture': 'no editor yet: how sure two items sharing one picture must be',
  'pricebook.art.jev.sameGroup': 'no editor yet: how sure a proposed price book group must be',
  'pricebook.art.jev.libraryMatch': 'no editor yet: how sure a library picture’s match must be',
  'pricebook.art.jev.generic': 'no editor yet: how sure generic words for the library must be',
  'pricebook.art.check': 'no editor yet: where a picture passes its check',
  // maps (BL-018): Setup › Maps and addresses is the maps design's package 10; until then each is the
  // server's default unless settings.set changes it (the switch itself is Booking and dispatch › Maps)
  'maps.check.bufferMinutes': 'no editor yet: Setup › Maps and addresses (the drive check’s buffer)',
  'maps.check.rushHour': 'no editor yet: Setup › Maps and addresses (the rush-hour allowance)',
  'maps.check.arriveBy': 'no editor yet: Setup › Maps and addresses (arrive by)',
};

/** The sign-in methods (decision 19), in the order the sign-in page shows them. */
export const SIGN_IN_METHODS: readonly (readonly [string, string])[] = [
  ['password', 'A password'],
  ['google', 'Google'],
  ['microsoft', 'Microsoft'],
  ['email_code', 'A code by email'],
  ['sms_code', 'A code by text'],
];

/** Turn one method on or off, kept in page order; the last one can't be turned off. */
export function toggleMethod(methods: readonly string[], method: string): string[] {
  const next = methods.includes(method) ? methods.filter((m) => m !== method) : [...methods, method];
  if (next.length === 0) return [...methods];
  const order = SIGN_IN_METHODS.map(([m]) => m);
  return next.sort((a, b) => order.indexOf(a) - order.indexOf(b));
}

/**
 * Whether this person may change a rule: by the permission the server says it needs (its row's
 * editPermission: settings.edit, or an owner-only one such as payroll.send), whichever role gives it
 * (QA-681). Without its row, owner-only rules need the owner and the rest settings.edit.
 */
export const ruleEditable = (rule: Rule, rights: Rights, row?: Pick<SettingRow, 'editPermission'>) =>
  row && rights.held ? rights.held.includes(row.editPermission) : rule.ownerOnly ? rights.owner : rights.edit;

/** Dollars typed by a person ↔ the cents a setting stores. */
export function dollarsToCents(text: string): number | null {
  const t = text.replace(/[$,\s]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  const [whole, frac = ''] = t.split('.');
  return Number(whole) * 100 + Number(frac.padEnd(2, '0'));
}
export const centsToDollars = (cents: number) => (cents / 100).toFixed(2);

/** agents.receptionist.hours: the office's hours and days (1 Monday … 7 Sunday) it answers outside. */
export interface AnswerHours {
  mode: 'after_hours' | 'always';
  open: string;
  close: string;
  days: number[];
}

export const WEEKDAYS: readonly (readonly [number, string])[] = [
  [1, 'Mon'],
  [2, 'Tue'],
  [3, 'Wed'],
  [4, 'Thu'],
  [5, 'Fri'],
  [6, 'Sat'],
  [7, 'Sun'],
];

export interface MatrixPermission {
  key: string;
  description: string;
  ownerOnly: boolean;
  systemOnly: boolean;
  grantable?: boolean;
}

const GROUP_OF: Record<string, string> = {
  settings: 'Company and people',
  org: 'Company and people',
  staff: 'Company and people',
  branding: 'Company and people',
  roles: 'Company and people',
  permissions: 'Company and people',
  audit: 'Company and people',
  approvals: 'Company and people',
  agents: 'Company and people',
  customers: 'Customers and calls',
  calls: 'Customers and calls',
  recordings: 'Customers and calls',
  leads: 'Customers and calls',
  messages: 'Customers and calls',
  jobs: 'Jobs and booking',
  tasks: 'Jobs and booking',
  appointments: 'In the field',
  estimates: 'In the field',
  forms: 'In the field',
  materials: 'In the field',
  media: 'In the field',
  notes: 'In the field',
  time: 'In the field',
  pricebook: 'Pricebook, stock and purchasing',
  inventory: 'Pricebook, stock and purchasing',
  purchasing: 'Pricebook, stock and purchasing',
  migration: 'ServiceTitan migration',
};

/** Permissions a person can hold, in reading groups; system-only ones are left out (QA-031). */
export function matrixGroups(
  perms: readonly MatrixPermission[],
): { title: string; permissions: MatrixPermission[] }[] {
  const out = new Map<string, MatrixPermission[]>();
  for (const p of perms) {
    if (p.systemOnly || p.key === 'system.ping') continue;
    const g = GROUP_OF[p.key.split('.')[0]!] ?? 'Money';
    out.set(g, [...(out.get(g) ?? []), p]);
  }
  const order = [
    'Company and people',
    'Customers and calls',
    'Jobs and booking',
    'In the field',
    'Money',
    'Pricebook, stock and purchasing',
    'ServiceTitan migration',
  ];
  return order.filter((t) => out.has(t)).map((title) => ({ title, permissions: out.get(title)! }));
}

/** One thing a person may do, and the roles it comes from (BL-137). */
export interface CanDo {
  key: string;
  description: string;
  ownerOnly: boolean;
  /** The names of the roles that give it, or "Given before roles" for a grant still running. */
  from: string[];
}

/** What a grant from before roles held owner-only permissions is called beside role names. */
export const GIVEN_BEFORE = 'Given before roles';

/**
 * BL-137: what a person may do is what any of their roles allows (the session's union, packages/auth
 * permissionsOf), with any grant still running: by the matrix's groups, each with the roles it comes
 * from, so taking one role away shows what goes (what no other of their roles gives).
 */
export function whatTheyCanDo(
  roleKeys: readonly string[],
  roles: readonly { key: string; name: string; permissions: readonly string[] }[],
  perms: readonly MatrixPermission[],
  grants: readonly { permission: string }[] = [],
): { title: string; items: CanDo[] }[] {
  const from = new Map<string, string[]>();
  const add = (p: string, why: string) => {
    const was = from.get(p) ?? [];
    if (!was.includes(why)) from.set(p, [...was, why]);
  };
  for (const k of roleKeys) {
    const role = roles.find((r) => r.key === k);
    for (const p of role?.permissions ?? []) add(p, role!.name);
  }
  for (const g of grants) add(g.permission, GIVEN_BEFORE);
  return matrixGroups(perms.filter((p) => from.has(p.key))).map((g) => ({
    title: g.title,
    items: g.permissions.map((p) => ({
      key: p.key,
      description: p.description,
      ownerOnly: p.ownerOnly,
      from: from.get(p.key)!,
    })),
  }));
}

/** Who holds each role (BL-137's role editor): the people whose roles include it, by name. */
export function holdersOf(
  roleKey: string,
  people: readonly { displayName: string; roleKeys: readonly string[] | null }[],
): string[] {
  return people.filter((p) => p.roleKeys?.includes(roleKey)).map((p) => p.displayName);
}

/** The roles a person could be given next: every role they don't hold yet. */
export function rolesToAdd<R extends { key: string }>(held: readonly string[], roles: readonly R[]): R[] {
  return roles.filter((r) => !held.includes(r.key));
}

/**
 * What the signed-in person may change on these pages, by the permissions they hold (QA-681), whichever
 * role gives them: `edit` settings.edit (the owner and managers), `owner` roles.edit (what stays with
 * the Owner role: roles and who holds them). A rule the server lists says its own permission
 * (ruleEditable). The server checks again.
 */
export function settingsRights(held: readonly string[]): Rights {
  return { edit: held.includes('settings.edit'), owner: held.includes('roles.edit'), held };
}

/** Who may change what on a settings page (settingsRights). */
export interface Rights {
  edit: boolean;
  owner: boolean;
  /** The permissions held: a rule the server lists is theirs to change when they hold its editPermission. */
  held?: readonly string[];
}

export interface SignInState {
  status: 'invited' | 'active' | 'disabled';
  invitedAt: string | null;
  expiresAt: string | null;
  lapsed: boolean;
  /** Turned off by the owner since then (people.disable). */
  disabledAt?: string | null;
  /** An invitation as owner waiting for an owner's yes (QA-355). */
  ownerConfirmPending?: boolean;
}

/** Where a person's sign-in stands, for People (QA-322); null when it's simply on, or there is none. */
export function signInWords(s: SignInState | null, timeZone: string): string | null {
  if (!s || s.status === 'active') return null;
  const day = (iso: string) =>
    new Intl.DateTimeFormat('en-CA', { timeZone, month: 'short', day: 'numeric' }).format(new Date(iso));
  if (s.status === 'disabled')
    return s.disabledAt ? `Signed in: off since ${day(s.disabledAt)}` : 'Signed in: off';
  if (s.lapsed && s.expiresAt) return `Invitation ended ${day(s.expiresAt)} · not signed in`;
  if (s.ownerConfirmPending)
    return s.invitedAt
      ? `Invited as owner ${day(s.invitedAt)} · waits for an owner’s OK`
      : 'Invited as owner · waits for an owner’s OK';
  return s.invitedAt ? `Invited ${day(s.invitedAt)} · not signed in yet` : 'Invited · not signed in yet';
}

/** What the office is told once someone is invited: emailed, or what to tell them (no email sent). */
export function invitedWords(
  r: { emailed: boolean; signInAt: string | null },
  name: string,
  email: string,
): string {
  if (r.emailed) return `Invitation sent to ${email}. ${name} signs in with that email.`;
  const where = r.signInAt ? r.signInAt.replace(/^https?:\/\//, '') : 'your company’s Flowology address';
  return `Ask ${name} to sign in at ${where} with Google or Microsoft using this email: ${email}.`;
}

/** The end of a company-local day, for a grant that lasts "until" that date. */
export function endOfDayIso(date: string, timeZone: string): string {
  // The instant of 23:59 local on `date`, found by stepping from UTC midnight in whole minutes.
  const guess = new Date(`${date}T23:59:00Z`);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(guess);
  const get = (t: string) => Number(parts.find((p) => p.type === t)!.value);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'));
  const offset = asUtc - guess.getTime();
  return new Date(guess.getTime() - offset).toISOString();
}

/** A setting's "HH:MM" as the office reads it: 21:00 is 9:00 p.m. */
export function hourWords(hhmm: string): string {
  const [h = 0, m = 0] = hhmm.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'a.m.' : 'p.m.'}`;
}

/**
 * A rule's value in words, for someone who reads the rules but doesn't change them: they see what
 * the company has set, not a field they can't type in.
 */
export function settingWords(
  control: Control,
  value: unknown,
  staff: readonly { id: string; displayName: string }[],
): string {
  switch (control.kind) {
    case 'bool':
      return value === true ? 'On' : 'Off';
    case 'select':
      return control.options.find(([v]) => String(v) === String(value))?.[1] ?? String(value);
    case 'number':
      return value === null || value === undefined ? 'Not set' : `${String(value)} ${control.unit}`;
    case 'money':
      if ((value === null || value === undefined) && control.none) return control.none;
      return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(
        Number(value) / 100,
      );
    case 'text':
      return typeof value === 'string' && value ? value : 'Not set';
    case 'quietHours': {
      const q = value as { start: string; end: string } | null;
      return q ? `${hourWords(q.start)} to ${hourWords(q.end)}` : 'None';
    }
    case 'answerHours': {
      const a = value as AnswerHours;
      if (a.mode === 'always') return 'Always';
      const days = WEEKDAYS.filter(([d]) => a.days.includes(d)).map(([, l]) => l);
      return `Outside ${hourWords(a.open)} to ${hourWords(a.close)}, ${days.join(', ') || 'no days'}`;
    }
    case 'workingHours': {
      const w = value as { open: string; close: string; days: number[] };
      const days = WEEKDAYS.filter(([d]) => w.days.includes(d)).map(([, l]) => l);
      return `${hourWords(w.open)} to ${hourWords(w.close)}, ${days.join(', ') || 'no days'}`;
    }
    case 'methods': {
      const on = value as string[];
      return SIGN_IN_METHODS.filter(([m]) => on.includes(m))
        .map(([, l]) => l)
        .join(', ');
    }
    case 'financing':
      return financingWords(value as FinancingSetting);
    case 'alertTo':
      return value === 'managers'
        ? 'The owner and managers'
        : (staff.find((s) => s.id === value)?.displayName ?? 'Someone no longer on staff');
    case 'paragraph':
      return typeof value === 'string' && value ? value : 'Not set';
    case 'words':
      return Array.isArray(value) && value.length ? value.join(', ') : 'None';
    case 'time':
      return typeof value === 'string' ? hourWords(value) : 'Not set';
    case 'colour':
      return typeof value === 'string' ? value : 'Flowology’s own';
    case 'overtime':
      return overtimeWords(value as Overtime);
    case 'twoFactor':
      return twoFactorWords(value as TwoFactor);
  }
}

/** payroll.overtime: minutes over which each rate starts. */
export interface Overtime {
  dailyOvertimeAfter: number;
  dailyDoubleAfter: number;
  weeklyOvertimeAfter: number;
}
/** BC's overtime (ESA ss.35 and 40): time and a half past 8 a day, double past 12, time and a half past 40 a week. */
export const BC_RULES: Overtime = {
  dailyOvertimeAfter: 480,
  dailyDoubleAfter: 720,
  weeklyOvertimeAfter: 2400,
};
export const isBcRules = (o: Overtime | null | undefined) =>
  !!o &&
  o.dailyOvertimeAfter === BC_RULES.dailyOvertimeAfter &&
  o.dailyDoubleAfter === BC_RULES.dailyDoubleAfter &&
  o.weeklyOvertimeAfter === BC_RULES.weeklyOvertimeAfter;
const WEEK_DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;
/** Said beside the week while it's Sunday under BC's rules: why Sunday comes first. */
export const BC_WEEK_LINE = 'BC’s Employment Standards Act counts the overtime week from Sunday.';
/**
 * A plain warning beside the overtime week, never a refusal: under BC's rules a week that doesn't
 * start on Sunday isn't the Act's week (ESA s.1, "week"). Null when there's nothing to say.
 */
export function weekWarning(weekStartsOn: number, bc: boolean): string | null {
  return bc && weekStartsOn !== 0 && WEEK_DAY_NAMES[weekStartsOn]
    ? `BC’s Employment Standards Act counts overtime weeks from Sunday, not ${WEEK_DAY_NAMES[weekStartsOn]}. You can still choose it.`
    : null;
}
export const OVERTIME_PARTS: readonly (readonly [keyof Overtime, string])[] = [
  ['dailyOvertimeAfter', 'hours a day, time and a half'],
  ['dailyDoubleAfter', 'hours a day, double time'],
  ['weeklyOvertimeAfter', 'hours a week, time and a half'],
];
/** Minutes as the hours a person types: 480 → "8", 450 → "7.5". */
export const minutesAsHours = (m: number) => String(Math.round((m / 60) * 100) / 100);
/** Hours typed → whole minutes, or null when it isn't a number of hours. */
export function hoursAsMinutes(text: string): number | null {
  const t = text.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(Number(t) * 60);
}
export const overtimeWords = (o: Overtime) =>
  `${minutesAsHours(o.dailyOvertimeAfter)} a day, double after ${minutesAsHours(o.dailyDoubleAfter)}, ${minutesAsHours(o.weeklyOvertimeAfter)} a week`;

/** auth.twoFactor: who must use a second step, and whether only an authenticator app counts. */
export interface TwoFactor {
  mode: 'off' | 'optional' | 'roles' | 'everyone';
  roles: string[];
  appOnly: boolean;
}
export const TWO_FACTOR_MODES: readonly (readonly [TwoFactor['mode'], string])[] = [
  ['off', 'Off'],
  ['optional', 'Each person chooses'],
  ['roles', 'Required for some roles'],
  ['everyone', 'Required for everyone'],
];
/** The roles every company has; one of its own keeps its key in the list. */
export const TWO_FACTOR_ROLES: readonly (readonly [string, string])[] = [
  ['manager', 'Managers'],
  ['dispatcher', 'Dispatchers'],
  ['csr', 'CSRs'],
  ['technician', 'Technicians'],
  ['bookkeeper', 'Bookkeepers'],
  ['warehouse', 'Warehouse'],
];
export function twoFactorWords(t: TwoFactor): string {
  const mode = TWO_FACTOR_MODES.find(([m]) => m === t.mode)?.[1] ?? t.mode;
  const roles = t.roles.map((r) => TWO_FACTOR_ROLES.find(([k]) => k === r)?.[1] ?? r);
  const who = t.mode === 'roles' ? `: ${roles.join(', ') || 'no roles yet'}` : '';
  return `${mode}${who}${t.appOnly && t.mode !== 'off' ? ', authenticator app only' : ''}`;
}

/** The words a "words" control's text holds: one a line, or comma-separated; blanks dropped. */
export const wordsOf = (text: string, perLine = false): string[] =>
  text
    .split(perLine ? /\n/ : /[,\n]/)
    .map((w) => w.trim())
    .filter(Boolean);

/** The line a rules card shows when this person can change none of it: who does. */
export function rulesCardLine(
  rules: readonly Rule[],
  rights: Rights,
  rows?: readonly Pick<SettingRow, 'key' | 'editPermission'>[] | null,
): string | null {
  const rowOf = (r: Rule) => rows?.find((x) => x.key === r.key);
  if (!rules.length || rules.some((r) => ruleEditable(r, rights, rowOf(r)))) return null;
  return rules.every((r) => r.ownerOnly)
    ? 'Only the owner changes these.'
    : 'Only the owner or a manager changes these.';
}

/**
 * Whether a person's row offers "Sign out everywhere" (people.signOutEverywhere): to someone who
 * manages people (staff.edit), on a sign-in in use, and on an owner only for the owner. The server
 * decides; this keeps the button off rows it would refuse. Never on your own row.
 */
export function offersSignOut(
  viewer: { owner: boolean; managesPeople: boolean; staffId: string | null },
  person: { staffId: string; roleKeys: string[] | null; signIn: { status: string } | null },
): boolean {
  if (!viewer.managesPeople || person.signIn?.status !== 'active') return false;
  if (viewer.staffId !== null && viewer.staffId === person.staffId) return false;
  return viewer.owner || !(person.roleKeys ?? []).includes('owner');
}
